import { Download, Share2 } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import toast from 'react-hot-toast';
import { Input } from '@/components/Input';

const DOCS: Array<{
    id: string; name: string; type: string; size: string; folder: string;
    sharedWith: string[]; versions: number; updatedAt: string; updatedBy: string;
    tags: string[]; pinned: boolean;
}> = [];

const FOLDERS: Array<{
    id: string; name: string; icon: string; docs: number; color: string;
}> = [];

const typeIcon: Record<string, string> = { pdf: '', excel: '', doc: '', ppt: '', image: '', default: '' };
const typeColor: Record<string, string> = { pdf: 'text-destructive', excel: 'text-success', doc: 'text-primary', ppt: 'text-warning', image: 'text-primary', default: 'text-muted-foreground' };

export function DocumentLibrary() {
    const [activeTab, setActiveTab] = useState<'library' | 'folders' | 'shared' | 'recent'>('library');
    const [searchQuery, setSearchQuery] = useState('');
    const [folderFilter, setFolderFilter] = useState('All');

    const filtered = DOCS.filter(d => {
        const matchFolder = folderFilter === 'All' || d.folder === folderFilter;
        const matchSearch = !searchQuery || d.name.toLowerCase().includes(searchQuery.toLowerCase()) || d.tags.some(t => t.includes(searchQuery.toLowerCase()));
        return matchFolder && matchSearch;
    });

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Document <span className="text-foreground">Library</span></h1>
                    <p className="text-muted-foreground mt-1">Files · Folders · Version History · Shared Documents</p>
                </div>
                <div className="flex gap-3">
                    <Button variant="secondary" onClick={() => toast.success('New folder...')}>New Folder</Button>
                    <Button variant="primary" onClick={() => toast.success('Upload dialog...')}>Upload File</Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Documents', value: '42', color: 'text-primary' },
                    { label: 'Shared with Me', value: '18', color: 'text-success' },
                    { label: 'Storage Used', value: '2.4 GB', color: 'text-warning' },
                    { label: 'Versions Tracked', value: '128', color: 'text-foreground' },
                ].map((s, i) => (
                    <Card key={i}><CardContent className="p-5">
                        <p className="text-xs text-muted-foreground font-semibold mb-1">{s.label}</p>
                        <p className={`text-3xl font-semibold ${s.color}`}>{s.value}</p>
                    </CardContent></Card>
                ))}
            </div>

            <div className="flex gap-2 border-b border-border">
                {[
                    { id: 'library', label: 'Document Library' },
                    { id: 'folders', label: 'Folders' },
                    { id: 'shared', label: 'Shared with Me' },
                    { id: 'recent', label: 'Recently Updated' },
                ].map(t => (
                    <Button variant="unstyled" size="unstyled" key={t.id} onClick={() => setActiveTab(t.id as any)}
                        className={`pb-4 px-4 text-sm font-semibold transition-all relative ${activeTab === t.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}>
                        {t.label}
                        {activeTab === t.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />}
                    </Button>
                ))}
            </div>

            {activeTab === 'folders' && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {FOLDERS.map(f => (
                        <Card key={f.id} className="hover:-translate-y-1 transition-all cursor-pointer group" onClick={() => { setFolderFilter(f.name); setActiveTab('library'); }}>
                            <CardContent className="p-5">
                                <div className={`w-12 h-12 rounded-lg ${f.color} flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition-transform`}>{f.icon}</div>
                                <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{f.name}</p>
                                <p className="text-xs text-muted-foreground mt-1">{f.docs} documents</p>
                            </CardContent>
                        </Card>
                    ))}
                    <Card className="border-dashed hover:-translate-y-1 transition-all cursor-pointer" onClick={() => toast.success('Create folder...')}>
                        <CardContent className="p-5 flex flex-col items-center justify-center gap-2 min-h-[120px]">
                            <div className="w-12 h-12 rounded-lg border-2 border-dashed border-border flex items-center justify-center text-2xl text-muted-foreground">+</div>
                            <p className="font-semibold text-muted-foreground">New Folder</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {(activeTab === 'library' || activeTab === 'shared' || activeTab === 'recent') && (
                <div className="space-y-4">
                    <div className="flex gap-3 flex-wrap items-center">
                        <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search documents..."
                            className="px-4 py-2 bg-background border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 flex-1 max-w-xs" />
                        {activeTab === 'library' && (
                            <select value={folderFilter} onChange={e => setFolderFilter(e.target.value)}
                                className="bg-background border border-border rounded-lg px-3 py-2 text-foreground text-sm focus:outline-none">
                                <option value="All">All Folders</option>
                                {FOLDERS.map(f => <option key={f.id}>{f.name}</option>)}
                            </select>
                        )}
                    </div>

                    {/* Pinned section */}
                    {activeTab === 'library' && !searchQuery && folderFilter === 'All' && (
                        <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">Pinned</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                {DOCS.filter(d => d.pinned).map(doc => (
                                    <div key={doc.id} className="flex items-center gap-4 p-4 bg-primary/5 border border-primary/20 rounded-lg hover:border-primary/40 transition-colors cursor-pointer" onClick={() => toast.success(`Opening ${doc.name}...`)}>
                                        <span className={`text-2xl ${typeColor[doc.type]}`}>{typeIcon[doc.type]}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-foreground text-sm truncate">{doc.name}</p>
                                            <p className="text-xs text-muted-foreground">{doc.size} · {doc.updatedAt}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <Card>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-sm text-muted-foreground font-semibold bg-card/50 border-b border-border">
                                        <th className="text-left px-6 py-3">Name</th>
                                        <th className="text-left px-4 py-3">Folder</th>
                                        <th className="text-left px-4 py-3">Shared With</th>
                                        <th className="text-center px-4 py-3">Versions</th>
                                        <th className="text-left px-4 py-3">Updated</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border-12">
                                    {(activeTab === 'recent' ? [...filtered].sort((a, b) => a.updatedAt.localeCompare(b.updatedAt)) : filtered).map(doc => (
                                        <tr key={doc.id} className="hover:bg-card/30 transition-colors cursor-pointer group" onClick={() => toast.success(`Opening ${doc.name}...`)}>
                                            <td className="px-6 py-3">
                                                <div className="flex items-center gap-3">
                                                    <span className={`text-xl ${typeColor[doc.type]}`}>{typeIcon[doc.type]}</span>
                                                    <div>
                                                        <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{doc.name}</p>
                                                        <p className="text-xs text-muted-foreground">{doc.size} · {doc.tags.map(t => `#${t}`).join(' ')}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground">{doc.folder}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1 flex-wrap">
                                                    {doc.sharedWith.slice(0, 2).map(s => (
                                                        <span key={s} className="text-xs px-1.5 py-0.5 bg-card border border-border rounded-md text-muted-foreground">{s}</span>
                                                    ))}
                                                    {doc.sharedWith.length > 2 && <span className="text-xs text-muted-foreground">+{doc.sharedWith.length - 2}</span>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <Button variant="unstyled" size="unstyled" className="text-primary hover:underline font-semibold text-xs" onClick={e => { e.stopPropagation(); toast.success(`${doc.versions} versions — opening history...`); }}>v{doc.versions}</Button>
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground text-sm">
                                                <p>{doc.updatedAt}</p>
                                                <p className="text-muted-foreground">{doc.updatedBy}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Button variant="unstyled" size="unstyled" className="p-1.5 hover:bg-card rounded-lg text-muted-foreground hover:text-foreground text-xs" onClick={e => { e.stopPropagation(); toast.success('Downloading...'); }}><Download className="size-3.5" /></Button>
                                                    <Button variant="unstyled" size="unstyled" className="p-1.5 hover:bg-card rounded-lg text-muted-foreground hover:text-foreground text-xs" onClick={e => { e.stopPropagation(); toast.success('Share dialog...'); }}><Share2 className="size-3.5" /></Button>
                                                    <Button variant="unstyled" size="unstyled" className="p-1.5 hover:bg-card rounded-lg text-muted-foreground hover:text-foreground text-xs" onClick={e => { e.stopPropagation(); toast.success('More options...'); }}>⋮</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {filtered.length === 0 && (
                                        <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                                            <p className="text-3xl mb-2"></p>
                                            <p>No documents found</p>
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
